// Phase 11b-1 + 11-2 — operator admin schemas for caregiver banking
// verification + payout-frequency flip.
import { z } from 'zod';
import { PayoutFrequencySchema } from '../billing/enums.schema.js';
import { CaregiverStatusSchema } from './application.schema.js';
const ReasonSchema = z
    .string()
    .min(10, 'Reason must be at least 10 characters — document why')
    .max(500);
// ─────────────────────────────────────────────────────────────────────────────
// GET /admin/banking/pending-verification — operator-verify queue
// ─────────────────────────────────────────────────────────────────────────────
export const PendingBankingVerificationItemSchema = z.object({
    caregiverId: z.string().uuid(),
    caregiverName: z.string(),
    caregiverEmail: z.string().email(),
    status: CaregiverStatusSchema,
    eftSubmittedAt: z.string().datetime(),
    eftRejected: z.boolean(),
    eftRejectionReason: z.string().nullable(),
    hasVoidChequeOnFile: z.boolean(),
    /** Last shift's completedAt for operator context — null if caregiver has
     *  never completed a shift yet. */
    lastShiftAt: z.string().datetime().nullable(),
    currentPayoutFrequency: PayoutFrequencySchema,
});
export const PendingBankingVerificationListResponseSchema = z.object({
    items: z.array(PendingBankingVerificationItemSchema),
    totalCount: z.number().int().nonnegative(),
});
// ─────────────────────────────────────────────────────────────────────────────
// GET /admin/caregivers/:id/banking — decrypted view for operator keying
// into the bank's web portal (per 11-D + 11b-2).
// ─────────────────────────────────────────────────────────────────────────────
export const AdminCaregiverBankingResponseSchema = z.object({
    caregiverId: z.string().uuid(),
    eftSubmittedAt: z.string().datetime().nullable(),
    eftVerifiedAt: z.string().datetime().nullable(),
    eftRejectedAt: z.string().datetime().nullable(),
    eftRejectionReason: z.string().nullable(),
    /** Full decrypted transit (5 digits) — operator keys this into RBC. */
    transit: z.string().nullable(),
    /** Full decrypted institution (3 digits). */
    institution: z.string().nullable(),
    /** Full decrypted account number — sensitive; only operator-role sees this. */
    account: z.string().nullable(),
    /** Object-storage key for the uploaded void cheque image. */
    voidChequeObjectKey: z.string().nullable(),
    /** Short-TTL signed download URL for the void-cheque image. Audit H4 — this
     *  is the ONLY endpoint that surfaces it (moved off the plain detail response),
     *  so the raw banking PII stays behind the TotpFresh step-up gate. */
    voidChequeDownloadUrl: z.string().url().nullable(),
});
// ─────────────────────────────────────────────────────────────────────────────
// POST /admin/caregivers/:id/banking/verify — operator approves
// POST /admin/caregivers/:id/banking/reject — operator rejects with reason
// ─────────────────────────────────────────────────────────────────────────────
export const AdminBankingVerifyResponseSchema = z.object({
    caregiverId: z.string().uuid(),
    eftVerifiedAt: z.string().datetime(),
});
export const AdminBankingRejectRequestSchema = z.object({
    reason: ReasonSchema,
});
export const AdminBankingRejectResponseSchema = z.object({
    caregiverId: z.string().uuid(),
    eftRejectedAt: z.string().datetime(),
    eftRejectionReason: z.string(),
});
// ─────────────────────────────────────────────────────────────────────────────
// POST /admin/caregivers/:id/payout-frequency — operator flips BIWEEKLY↔WEEKLY
// per 11-2 (operator-controlled at v1).
// ─────────────────────────────────────────────────────────────────────────────
export const AdminPayoutFrequencyRequestSchema = z.object({
    frequency: PayoutFrequencySchema,
    reason: ReasonSchema,
});
export const AdminPayoutFrequencyResponseSchema = z.object({
    caregiverId: z.string().uuid(),
    payoutFrequency: PayoutFrequencySchema,
});
//# sourceMappingURL=banking-admin.schema.js.map